<template>
  <div class="homelab-container">
    <LayerNavigator :is-preview="isPreview"/>

    <!-- Header Section -->
    <header class="homelab-header">
      <div class="osi-badge">Infrastructure Overview</div>
      <h1>{{ $t('infra.title') }}</h1>
      <p class="subtitle">{{ $t('infra.subtitle') }}</p>
      <p class="intro-text">{{ $t('infra.intro') }}</p>
    </header>

    <!-- Large SVG Network Topology -->
    <section class="topology-section ln-glass-elevated">
      <div class="section-header">
        <span class="mdi mdi-sitemap-outline"></span>
        <h2>{{ $t('homelab.architectureTitle') }}</h2>
      </div>
      <HomeLabImage/>
      <p class="topology-caption">{{ $t('homelab.caption') }}</p>
    </section>

    <!-- Layered Architecture Overview -->
    <section class="layers-overview">
      <div class="section-header">
        <span class="mdi mdi-layers-triple-outline"></span>
        <h2>Architecture en Couches</h2>
      </div>
      
      <div class="layers-grid">
        <!-- Layer 0 -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l0.name')"
          :title="$t('infra.layers.l0.title')"
          :description="$t('infra.layers.l0.proof')"
          badge="OSI 1-2"
          icon="mdi-server-network"
          variant="ln-glass-teal"
          @click="goToLayer('infra-l0')"
        />

        <!-- Layer 1 -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l1.name')"
          :title="$t('infra.layers.l1.title')"
          description="Orchestration GitOps & Réseau Dual-Homed."
          badge="OSI 3-4"
          icon="mdi-kubernetes"
          variant="ln-glass-terracotta"
          @click="goToLayer('infra-l1')"
        />

        <!-- Layer 2 -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l2.name')"
          :title="$t('infra.layers.l2.title')"
          description="Persistence (Longhorn) & DBaaS via Operators."
          badge="OSI 5-6"
          icon="mdi-database"
          variant="ln-glass-stone"
          @click="goToLayer('infra-l2')"
        />

        <!-- Layer 3 -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l3.name')"
          :title="$t('infra.layers.l3.title')"
          description="Vault, Zitadel & Alerting Physique 'Nuke'."
          badge="OSI 7"
          icon="mdi-shield-lock"
          variant="ln-glass-elevated"
          @click="goToLayer('infra-l3')"
        />
      </div>
    </section>

    <!-- Core Competencies Reformat -->
    <section class="competencies-section">
      <div class="section-header">
        <span class="mdi mdi-shield-check-outline"></span>
        <h2>{{ $t('homelab.learningsTitle') }}</h2>
      </div>
      
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-server"
          :title="$t('homelab.competencies.sysadmin.title')"
          :description="$t('homelab.competencies.sysadmin.desc')"
        />
        <InfraCompetencyCard 
          icon="mdi-kubernetes"
          :title="$t('homelab.competencies.k8s.title')"
          :description="$t('homelab.competencies.k8s.desc')"
        />
        <InfraCompetencyCard 
          icon="mdi-infinity"
          :title="$t('homelab.competencies.devops.title')"
          :description="$t('homelab.competencies.devops.desc')"
        />
        <InfraCompetencyCard 
          icon="mdi-database-sync"
          :title="$t('homelab.competencies.storage.title')"
          :description="$t('homelab.competencies.storage.desc')"
        />
        <InfraCompetencyCard 
          icon="mdi-chart-bell-curve-cumulative"
          :title="$t('homelab.competencies.obs.title')"
          :description="$t('homelab.competencies.obs.desc')"
        />
        <InfraCompetencyCard 
          icon="mdi-lock-outline"
          :title="$t('homelab.competencies.secops.title')"
          :description="$t('homelab.competencies.secops.desc')"
        />
      </div>
    </section>

    <!-- Professional Context -->
    <section class="context-section ln-glass-elevated">
      <div class="section-header">
        <span class="mdi mdi-briefcase-outline"></span>
        <h2>{{ $t('homelab.contextTitle') }}</h2>
      </div>
      <div class="context-content">
        <p v-html="$t('homelab.context1')"></p>
        <p v-html="$t('homelab.context2')"></p>
      </div>
    </section>

    <!-- Final CTA -->
    <InfraLayerFooter 
      nextRoute="infra-l0"
      :nextLabel="'Commencer l\'exploration : ' + $t('infra.layers.l0.name')"
      @navigate="goToLayer"
    />
  </div>
</template>

<script lang="ts">
import {Vue, Component, Prop} from 'vue-facing-decorator';
import LayerNavigator from "@/component/common/LayerNavigator.vue";
import HomeLabImage from "@/component/common/HomeLabImage.vue";
import InfraLayerCard from "@/component/project/InfraLayerCard.vue";
import InfraCompetencyCard from "@/component/project/infra/InfraCompetencyCard.vue";
import InfraLayerFooter from "@/component/project/infra/InfraLayerFooter.vue";
import { useRouter } from 'vue-router';
import { usePostHog } from '@/composables/usePostHog';

@Component({
  components: {HomeLabImage, LayerNavigator, InfraLayerCard, InfraCompetencyCard, InfraLayerFooter}
})
export default class HomeLabComponent extends Vue {
  @Prop({ default: false }) private isPreview!: boolean;
  private router = useRouter();
  private posthog = usePostHog().posthog;

  private goToLayer(routeName: string) {
    this.posthog.capture('homelab_layer_clicked', { target_layer: routeName });
    this.router.push({ name: routeName });
    window.scrollTo(0, 0);
  }
}
</script>

<style scoped>
@import "@/assets/styles/infra-layers.css";

.homelab-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

/* === SHARED ELEMENTS === */
.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.section-header span.mdi {
  font-size: 2rem;
  color: var(--ln-teal-deep);
}

.section-header h2 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  padding: 0;
}

.section-header h2::after {
  display: none;
}

/* === HEADER === */
.homelab-header {
  text-align: center;
  margin-bottom: 5rem;
}

.osi-badge {
  display: inline-block;
  padding: 0.5rem 1.2rem;
  background: var(--ln-teal-deep);
  color: white;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.homelab-header h1 {
  font-size: clamp(2.5rem, 8vw, 4rem);
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.1;
}

.subtitle {
  font-size: 1.5rem;
  color: var(--ln-teal-deep);
  font-weight: 600;
  margin-bottom: 2rem;
}

.intro-text {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.2rem;
  color: var(--ln-text-secondary);
}

/* === TOPOLOGY === */
.topology-section {
  padding: 2rem;
  margin-bottom: 3rem;
  border-radius: var(--ln-radius-lg);
}

.topology-caption {
  text-align: center;
  margin-top: 1rem;
  color: var(--ln-stone-grey);
  font-style: italic;
}

/* === LAYERS GRID === */
.layers-overview {
  margin-bottom: 5rem;
}

.layers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
}

.layer-card {
  cursor: pointer;
  position: relative;
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  display: flex;
  flex-direction: column;
}

.layer-card:hover {
  transform: translateY(-10px);
}

.layer-number {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  font-size: 4rem;
  font-weight: 900;
  opacity: 0.1;
  line-height: 1;
}

.layer-content h3 {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--ln-text-primary);
}

.layer-content p {
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  margin-bottom: 2rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.explore-link {
  margin-top: auto;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ln-teal-deep);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* === COMPETENCIES === */
.competencies-section {
  margin-bottom: 5rem;
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.comp-item {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: var(--ln-radius-md);
}

.comp-item span.mdi {
  font-size: 2rem;
  color: var(--ln-teal-deep);
  background: var(--ln-glass-surface);
  padding: 0.8rem;
  border-radius: 1rem;
}

.comp-item strong {
  display: block;
  font-size: 1.1rem;
  margin-bottom: 0.3rem;
  color: var(--ln-text-primary);
}

.comp-item p {
  font-size: 0.9rem;
  color: var(--ln-text-secondary);
  margin: 0;
}

/* === CONTEXT === */
.context-section {
  padding: 3rem;
  margin-bottom: 5rem;
  border-radius: var(--ln-radius-lg);
}

.context-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

.context-content p {
  margin: 0;
  font-size: 1.1rem;
}

/* === RESPONSIVE === */
@media (max-width: 968px) {
  .context-content {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .homelab-container {
    padding: 2rem 1rem;
  }
  
  .homelab-header {
    margin-bottom: 3rem;
  }

  .topology-section {
    padding: 1.5rem;
  }

  .layers-grid {
    grid-template-columns: 1fr;
  }
  
  .competencies-grid {
    grid-template-columns: 1fr;
  }

  .next-layer-btn {
    padding: 1.2rem 2rem;
    width: 100%;
    justify-content: center;
  }
}
</style>
